import React, { useState, useEffect, useCallback } from 'react';
import {
  FluentProvider,
  webLightTheme,
  webDarkTheme,
  makeStyles,
  tokens,
  Text,
  Card,
  Button,
} from '@fluentui/react-components';
import { ShieldError24Regular } from '@fluentui/react-icons';

import { ConfirmDialog } from './components/ConfirmDialog';
import { ProgressSpinner } from './components/ProgressSpinner';
import { ResultMessage, ResultType } from './components/ResultMessage';

import { ConfigService } from '../services/ConfigService';
import { ReportService } from '../services/ReportService';
import { EmailAnalyzer } from '../services/EmailAnalyzer';
import { CampaignDetector } from '../services/CampaignDetector';
import type { PhisherConfig } from '../models/PhisherConfig';
import type { CampaignInfo, EmailAnalysis } from '../models/ReportPayload';

// Branding config from environment (set by build.ps1)
const DISPLAY_NAME = import.meta.env.VITE_DISPLAY_NAME || 'Report بلاغ';
const BRAND_COLOR = import.meta.env.VITE_BRAND_COLOR || '#0078d4';
const BRAND_TEXT_COLOR = import.meta.env.VITE_BRAND_TEXT_COLOR || '#ffffff';

const useStyles = makeStyles({
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalL,
    backgroundColor: BRAND_COLOR,
    color: BRAND_TEXT_COLOR,
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: 'inherit',
  },
  content: {
    flex: 1,
    padding: tokens.spacingVerticalL,
    overflowY: 'auto',
  },
  noEmail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
  },
  infoCard: {
    padding: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
  },
  infoRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: '80px',
  },
  value: {
    flex: 1,
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalL,
  },
});

type ReportState = 'initializing' | 'idle' | 'confirming' | 'loading' | 'success' | 'error';

interface EmailInfo {
  subject: string;
  from: string;
  fromEmail: string;
  to: string;
  dateReceived: string;
}

export const TaskPane: React.FC = () => {
  const styles = useStyles();

  // State
  const [state, setState] = useState<ReportState>('initializing');
  const [config, setConfig] = useState<PhisherConfig | null>(null);
  const [emailInfo, setEmailInfo] = useState<EmailInfo | null>(null);
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);
  const [analysis, setAnalysis] = useState<EmailAnalysis | null>(null);
  const [resultMessage, setResultMessage] = useState<{ type: ResultType; title: string; message: string; details?: string } | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Load current email information
  const loadEmailInfo = useCallback(async () => {
    const item = Office.context.mailbox?.item;
    if (!item) {
      setEmailInfo(null);
      return;
    }

    const info: EmailInfo = {
      subject: item.subject || '',
      from: item.from?.displayName || item.from?.emailAddress || '',
      fromEmail: item.from?.emailAddress || '',
      to: item.to?.map(r => r.emailAddress).join(', ') || '',
      dateReceived: item.dateTimeCreated?.toLocaleString() || '',
    };
    setEmailInfo(info);

    // Get email body for campaign detection and analysis
    const bodyResult = await ReportService.getEmailBody(item);

    // Detect campaign
    const campaign = CampaignDetector.detectFromHtml(bodyResult.html || bodyResult.text);
    setCampaignInfo(campaign);

    // Analyze email
    const attachments = item.attachments || [];
    const emailAnalysis = await EmailAnalyzer.analyze(bodyResult.html, attachments);
    setAnalysis(emailAnalysis);
  }, []);

  // Load configuration and email info on mount
  useEffect(() => {
    const init = async () => {
      // Detect dark mode
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(prefersDark);

      // Load config
      const cfg = await ConfigService.loadConfig();
      setConfig(cfg);

      // Load email info
      await loadEmailInfo();
      setState('idle');
    };

    Office.onReady(() => {
      init();
    });
  }, [loadEmailInfo]);

  // Handle report button click
  const handleReportClick = () => {
    setState('confirming');
  };

  // Handle confirm dialog
  const handleConfirm = async () => {
    setState('loading');

    try {
      const mailbox = Office.context.mailbox;
      const item = mailbox.item;

      if (!item || !config || !campaignInfo || !analysis) {
        throw new Error('Missing required data for report');
      }

      // Try to get EML content
      const emlContent = await ReportService.getEmailAsEml(item);

      // Re-check campaign detection using raw EML if available.
      // Outlook Web sanitizes HTML returned by body.getAsync, which can strip
      // hidden elements containing campaign markers. The raw EML is unmodified.
      let resolvedCampaignInfo = campaignInfo;
      if (emlContent && !campaignInfo.isCampaign) {
        const emlDetected = CampaignDetector.detectFromEml(emlContent);
        if (emlDetected.isCampaign) {
          resolvedCampaignInfo = emlDetected;
        }
      }

      // Submit report
      const result = await ReportService.submitReport(
        mailbox,
        item,
        config,
        resolvedCampaignInfo,
        analysis,
        emlContent || undefined
      );

      if (result.success) {
        // Increment counter
        ConfigService.incrementReportsCounter();

        setState('success');
        const baseMessage = campaignInfo.isCampaign
          ? 'Great job! You correctly identified this training phishing email.'
          : 'Thank you for reporting this suspicious email. Our security team will investigate.';
        setResultMessage({
          type: 'success',
          title: 'Report Submitted',
          message: result.message || baseMessage,
        });
      } else {
        setState('error');
        setResultMessage({
          type: 'error',
          title: 'Report Failed',
          message: result.message || 'Failed to submit the report.',
          details: result.error,
        });
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      setState('error');
      setResultMessage({
        type: 'error',
        title: 'Error',
        message: 'An unexpected error occurred while submitting the report.',
        details: error instanceof Error ? error.message : String(error),
      });
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setState('idle');
  };

  // Handle close result
  const handleClose = () => {
    setState('idle');
    setResultMessage(null);
  };

  // Handle retry
  const handleRetry = () => {
    setState('confirming');
    setResultMessage(null);
  };

  // Render content based on state
  const renderContent = () => {
    if (state === 'initializing') {
      return <ProgressSpinner message="Loading email information..." />;
    }

    if (!emailInfo) {
      return (
        <div className={styles.noEmail}>
          <ShieldError24Regular style={{ fontSize: 48 }} />
          <Text>No email selected</Text>
          <Text size={200}>Please select an email to report as phishing.</Text>
        </div>
      );
    }

    switch (state) {
      case 'loading':
        return <ProgressSpinner message="Submitting report to security team..." />;

      case 'success':
      case 'error':
        return resultMessage ? (
          <ResultMessage
            type={resultMessage.type}
            title={resultMessage.title}
            message={resultMessage.message}
            details={resultMessage.details}
            onClose={handleClose}
            onRetry={state === 'error' ? handleRetry : undefined}
          />
        ) : null;

      default:
        return (
          <>
            <Card className={styles.infoCard}>
              <div className={styles.infoRow}>
                <Text className={styles.label}>From:</Text>
                <Text className={styles.value}>{emailInfo.from}</Text>
              </div>
              <div className={styles.infoRow}>
                <Text className={styles.label}>Subject:</Text>
                <Text className={styles.value}>{emailInfo.subject || '(No subject)'}</Text>
              </div>
              <div className={styles.infoRow}>
                <Text className={styles.label}>Received:</Text>
                <Text className={styles.value}>{emailInfo.dateReceived}</Text>
              </div>
              {analysis && (
                <>
                  <div className={styles.infoRow}>
                    <Text className={styles.label}>URLs:</Text>
                    <Text className={styles.value}>{analysis.urls.length} found</Text>
                  </div>
                  <div className={styles.infoRow}>
                    <Text className={styles.label}>Attachments:</Text>
                    <Text className={styles.value}>{analysis.attachments.length} found</Text>
                  </div>
                </>
              )}
            </Card>

            <div className={styles.actions}>
              <Button appearance="primary" size="large" onClick={handleReportClick}>
                Report as Phishing
              </Button>
            </div>

            <ConfirmDialog
              isOpen={state === 'confirming'}
              subject={emailInfo.subject}
              from={emailInfo.from}
              isCampaign={campaignInfo?.isCampaign || false}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
            />
          </>
        );
    }
  };

  return (
    <FluentProvider theme={isDarkMode ? webDarkTheme : webLightTheme}>
      <div className={styles.container}>
        <div className={styles.header}>
          <ShieldError24Regular />
          <Text className={styles.headerTitle}>{DISPLAY_NAME}</Text>
        </div>
        <div className={styles.content}>
          {renderContent()}
        </div>
      </div>
    </FluentProvider>
  );
};
